import type { EChartsOption } from "echarts";
import { designTokens } from "theme/customTheme";
import { ChartValue } from "apps/ai-metrics/types/aiMetrics.types";

export const SERIES_COLORS = [
  designTokens.accent[500],
  designTokens.neutral[400],
  designTokens.primary[600],
  designTokens.accent[300],
  designTokens.neutral[600],
  designTokens.primary[400],
  designTokens.accent[700],
  designTokens.neutral[300],
  designTokens.primary[800],
];

export const COMPARISON_COLORS = {
  prior: SERIES_COLORS[1],
  current: SERIES_COLORS[0],
};

export function comparisonColorAt(index: number, total: number): string {
  if (total < 2) return COMPARISON_COLORS.current;
  return index === 0 ? COMPARISON_COLORS.prior : COMPARISON_COLORS.current;
}

const FONT = designTokens.fontFamily;
const AXIS_COLOR = designTokens.neutral[300];
const SPLIT_LINE_COLOR = designTokens.neutral[100];
const LABEL_COLOR = designTokens.neutral[500];

export const SEVERITY_COLORS = {
  critical: "#B91C1C",
  high: "#F97316",
  medium: "#FBBF24",
  low: "#FEF08A",
};

export const SEVERITY_MAP: Record<string, { label: string; color: string }> = {
  "1 - Critical": { label: "P1 Critical", color: SEVERITY_COLORS.critical },
  "2 - High": { label: "P2 High", color: SEVERITY_COLORS.high },
  "3 - Medium": { label: "P3 Medium", color: SEVERITY_COLORS.medium },
  "4 - Low": { label: "P4 Low", color: SEVERITY_COLORS.low },
};

export function formatMonthLabels(labels: string[]): string[] {
  const years = new Set(
    labels.map((l) => l?.slice(0, 4)).filter((y): y is string => !!y),
  );
  const multiYear = years.size > 1;
  return labels.map((label) => {
    if (!label) return label;
    const match = /^(\d{4})-(\d{2})$/.exec(label);
    if (!match) return label;
    const [, year, month] = match;
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString(undefined, {
      month: "short",
      ...(multiYear ? { year: "numeric" } : {}),
    });
  });
}

function baseGrid() {
  return { left: 16, right: 16, top: 8, bottom: 0, containLabel: true };
}

function baseCategoryAxis(data: string[]) {
  return {
    type: "category" as const,
    data,
    axisLine: { lineStyle: { color: AXIS_COLOR } },
    axisTick: { show: false },
    axisLabel: {
      fontFamily: FONT,
      fontSize: 11,
      color: LABEL_COLOR,
    },
  };
}

function baseValueAxis(name?: string) {
  return {
    type: "value" as const,
    name,
    nameLocation: "middle" as const,
    nameGap: 44,
    nameRotate: 90,
    nameTextStyle: { fontFamily: FONT, fontSize: 11, color: LABEL_COLOR },
    splitLine: { lineStyle: { color: SPLIT_LINE_COLOR } },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { fontFamily: FONT, fontSize: 11, color: LABEL_COLOR },
  };
}

function baseLegend(names: string[]) {
  return {
    data: names,
    bottom: 4,
    textStyle: {
      fontFamily: FONT,
      fontSize: 12,
      color: designTokens.neutral[600],
    },
    icon: "circle",
    itemWidth: 8,
    itemHeight: 8,
  };
}

function baseTooltip() {
  return { trigger: "axis" as const, confine: true };
}

interface TooltipParam {
  seriesName?: string;
  seriesIndex?: number;
  dataIndex?: number;
  value?: number | null;
  marker?: string;
  axisValueLabel?: string;
}

function pointLabelTooltip(
  seriesPointLabels: ((string | null)[] | undefined)[],
): EChartsOption["tooltip"] {
  return {
    trigger: "axis",
    confine: true,
    formatter: (params) => {
      const rows = (
        Array.isArray(params) ? params : [params]
      ) as TooltipParam[];
      const header = rows[0]?.axisValueLabel ?? "";
      const lines = rows
        .filter((p) => p.value != null)
        .map((p) => {
          const extra =
            p.seriesIndex != null && p.dataIndex != null
              ? seriesPointLabels[p.seriesIndex]?.[p.dataIndex]
              : null;
          const name = extra ? `${p.seriesName} (${extra})` : p.seriesName;
          return `${p.marker ?? ""}${name}: ${p.value}`;
        });
      return lines.length ? [header, ...lines].join("<br/>") : "";
    },
  };
}

export interface ILineSeries {
  name: string;
  data: ChartValue[];
  dashed?: boolean;
  showArea?: boolean;
  color?: string;
  pointLabels?: (string | null)[];
}

export function createLineOption(config: {
  xLabels: string[];
  series: ILineSeries[];
  yAxisLabel?: string;
}): EChartsOption {
  return {
    tooltip: pointLabelTooltip(config.series.map((s) => s.pointLabels)),
    legend: { show: false },
    grid: baseGrid(),
    xAxis: baseCategoryAxis(config.xLabels),
    yAxis: baseValueAxis(config.yAxisLabel),
    series: config.series.map((s, i) => {
      const lineColor = s.color ?? SERIES_COLORS[i % SERIES_COLORS.length];
      return {
        name: s.name,
        type: "line",
        data: s.data,
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          color: lineColor,
          type: s.dashed ? "dashed" : "solid",
          width: 2.5,
        },
        itemStyle: { color: lineColor },
        areaStyle: s.showArea ? { color: lineColor, opacity: 0.08 } : undefined,
      };
    }),
  };
}

export interface IBarSeries {
  name: string;
  data: ChartValue[];
  /** Explicit bar color; falls back to the index-based palette color. */
  color?: string;
  /** Per-point tooltip labels (e.g. real prior sprint name); index-aligned. */
  pointLabels?: (string | null)[];
}

export function createGroupedBarOption(config: {
  xLabels: string[];
  series: IBarSeries[];
  yAxisLabel?: string;
}): EChartsOption {
  return {
    tooltip: pointLabelTooltip(config.series.map((s) => s.pointLabels)),
    legend: baseLegend(config.series.map((s) => s.name)),
    grid: baseGrid(),
    xAxis: baseCategoryAxis(config.xLabels),
    yAxis: baseValueAxis(config.yAxisLabel),
    series: config.series.map((s, i) => ({
      name: s.name,
      type: "bar",
      data: s.data,
      barMaxWidth: 28,
      itemStyle: {
        color: s.color ?? SERIES_COLORS[i % SERIES_COLORS.length],
        borderRadius: [4, 4, 0, 0],
      },
    })),
  };
}

export function createHorizontalBarOption(config: {
  yLabels: string[];
  series: IBarSeries[];
  xMax?: number;
  xAxisLabel?: string;
}): EChartsOption {
  return {
    tooltip: baseTooltip(),
    legend: baseLegend(config.series.map((s) => s.name)),
    grid: baseGrid(),
    xAxis: {
      ...baseValueAxis(config.xAxisLabel),
      max: config.xMax ?? 100,
    },
    yAxis: {
      type: "category" as const,
      data: config.yLabels,
      axisLine: { lineStyle: { color: AXIS_COLOR } },
      axisTick: { show: false },
      axisLabel: { fontFamily: FONT, fontSize: 11, color: LABEL_COLOR },
    },
    series: config.series.map((s, i) => ({
      name: s.name,
      type: "bar",
      data: s.data,
      barMaxWidth: 24,
      itemStyle: {
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        borderRadius: [0, 4, 4, 0],
      },
    })),
  };
}

export interface IStackedBarSeries {
  name: string;
  data: ChartValue[];
  color: string;
}

export function createStackedBarOption(config: {
  xLabels: string[];
  series: IStackedBarSeries[];
  yAxisLabel?: string;
}): EChartsOption {
  return {
    tooltip: baseTooltip(),
    legend: baseLegend(config.series.map((s) => s.name)),
    grid: baseGrid(),
    xAxis: baseCategoryAxis(config.xLabels),
    yAxis: baseValueAxis(config.yAxisLabel),
    series: config.series.map((s) => ({
      name: s.name,
      type: "bar",
      stack: "total",
      data: s.data,
      barMaxWidth: 60,
      itemStyle: { color: s.color, borderRadius: 0 },
    })),
  };
}

export const SPRINT_DELIVERY_COLORS = {
  throughput: designTokens.primary[700],
  velocity: designTokens.accent[300],
  spend: designTokens.semantic.warning,
};

const SPEND_AXIS_INDEX = 1;
const BAR_VALUE_LABEL_FONT_SIZE = 11;
const SPEND_AREA_OPACITY = 0.07;

// An x label may carry the sprint name and its date range on two lines, so the
// axis needs a line height; the tooltip puts the same two parts side by side.
const SPRINT_AXIS_LINE_HEIGHT = 14;
const SPRINT_LABEL_SEPARATOR = " · ";

export interface ISprintDeliverySeries {
  name: string;
  data: ChartValue[];
  color: string;
  /** Appended to the value in the tooltip, e.g. "items" or "pts". */
  unit?: string;
  /**
   * Bars sharing a stack name pile up; different stacks sit side by side.
   * The per-team view stacks by team, so every team gets its own bar with its
   * metrics inside it rather than a shared bar per metric.
   */
  stack?: string;
  /** Fades a segment so two in the same colour stay tellable apart. */
  opacity?: number;
}

/**
 * Delivery bars per sprint with the AI spend line on its own right-hand axis.
 *
 * Spend is orders of magnitude larger than an item count, so it cannot share the
 * left axis - a single axis would flatten the bars into the baseline. The left
 * axis therefore carries no tick labels at all: the bars are annotated with
 * their own values, and the only axis a reader needs to scale is the spend one.
 *
 * `spend` takes a list because the per-team view draws one line per team; the
 * combined view passes a single-entry list. `showBarValueLabels` is turned off
 * for stacked bars, where a label per segment would cover the bar it annotates.
 */
export function createSprintDeliveryOption(config: {
  xLabels: string[];
  bars: ISprintDeliverySeries[];
  spend: ISprintDeliverySeries[];
  spendAxisName: string;
  formatSpend: (value: number) => string;
  showBarValueLabels?: boolean;
}): EChartsOption {
  const { spend, formatSpend, showBarValueLabels = true } = config;
  const spendByName = new Map(spend.map((series) => [series.name, series]));
  const spendColor = spend[0]?.color ?? SPRINT_DELIVERY_COLORS.spend;
  const sprintAxis = baseCategoryAxis(config.xLabels);
  return {
    tooltip: {
      trigger: "axis",
      confine: true,
      formatter: (params) => {
        const rows = (
          Array.isArray(params) ? params : [params]
        ) as TooltipParam[];
        const header = (rows[0]?.axisValueLabel ?? "").replace(
          /\n/g,
          SPRINT_LABEL_SEPARATOR,
        );
        const lines = rows
          .filter((row) => row.value != null)
          .map((row) => {
            const spendSeries = row.seriesName
              ? spendByName.get(row.seriesName)
              : undefined;
            const value = spendSeries
              ? formatSpend(row.value as number)
              : String(row.value);
            const series = config.bars.find(
              (bar) => bar.name === row.seriesName,
            );
            const unit = spendSeries ? spendSeries.unit : series?.unit;
            return `${row.marker ?? ""}${row.seriesName}: ${value}${unit ? ` ${unit}` : ""}`;
          });
        return lines.length ? [header, ...lines].join("<br/>") : "";
      },
    },
    legend: { show: false },
    grid: { left: 16, right: 16, top: 24, bottom: 0, containLabel: true },
    xAxis: {
      ...sprintAxis,
      axisLabel: {
        ...sprintAxis.axisLabel,
        lineHeight: SPRINT_AXIS_LINE_HEIGHT,
      },
    },
    yAxis: [
      { ...baseValueAxis(), axisLabel: { show: false } },
      {
        ...baseValueAxis(config.spendAxisName),
        position: "right",
        splitLine: { show: false },
        nameRotate: -90,
        nameTextStyle: {
          fontFamily: FONT,
          fontSize: BAR_VALUE_LABEL_FONT_SIZE,
          color: spendColor,
        },
        axisLabel: {
          fontFamily: FONT,
          fontSize: BAR_VALUE_LABEL_FONT_SIZE,
          color: spendColor,
          formatter: (value: number) => formatSpend(value),
        },
      },
    ],
    series: [
      ...config.bars.map((bar) => ({
        name: bar.name,
        type: "bar" as const,
        data: bar.data,
        stack: bar.stack,
        barMaxWidth: 28,
        itemStyle: {
          color: bar.color,
          opacity: bar.opacity,
          borderRadius: [4, 4, 0, 0] as [number, number, number, number],
        },
        label: {
          show: showBarValueLabels,
          position: "top" as const,
          fontFamily: FONT,
          fontSize: BAR_VALUE_LABEL_FONT_SIZE,
          fontWeight: designTokens.fontWeight.semibold,
          color: designTokens.primary[800],
        },
      })),
      ...spend.map((series) => ({
        name: series.name,
        type: "line" as const,
        yAxisIndex: SPEND_AXIS_INDEX,
        data: series.data,
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { color: series.color, type: "dashed" as const, width: 2 },
        itemStyle: { color: series.color },
        areaStyle:
          spend.length === 1
            ? { color: series.color, opacity: SPEND_AREA_OPACITY }
            : undefined,
      })),
    ],
  };
}

export interface IGroupedStackTeamSeries {
  team: string;
  current: ChartValue[];
  prior: ChartValue[];
  color: string;
}

export function createGroupedStackedBarOption(config: {
  xLabels: string[];
  teams: IGroupedStackTeamSeries[];
  currentLabel: string;
  priorLabel: string;
  yAxisLabel?: string;
}): EChartsOption {
  const CURRENT = "current";
  const PRIOR = "prior";
  const series = config.teams.flatMap((t) => [
    {
      name: t.team,
      type: "bar" as const,
      stack: PRIOR,
      data: t.prior,
      barMaxWidth: 28,
      itemStyle: { color: t.color, opacity: 0.45, borderRadius: 0 },
      legendHoverLink: false,
    },
    {
      name: t.team,
      type: "bar" as const,
      stack: CURRENT,
      data: t.current,
      barMaxWidth: 28,
      itemStyle: { color: t.color, borderRadius: 0 },
    },
  ]);
  return {
    tooltip: baseTooltip(),
    legend: baseLegend(config.teams.map((t) => t.team)),
    grid: baseGrid(),
    xAxis: baseCategoryAxis(config.xLabels),
    yAxis: baseValueAxis(config.yAxisLabel),
    series,
  };
}
